import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { describeLoadFailure, jsonOrThrow } from '../apiJson';
import { trackGenerateLead } from '../ga';
import {
  BellIcon,
  SEARCH_ALERT_BENEFITS,
  searchAlertSignupSuccessMessage,
} from '../searchAlertBenefits';
import { FacebookGeneralDealsNudge } from './FacebookGeneralDealsNudge';
import './FavoriteSignupModal.css';
import './SearchAlertSignupPanel.css';
import './SearchAlertSignupModal.css';

/**
 * @param {{
 *   onClose: () => void;
 *   searchPath: string;
 *   scopeLabel: string;
 *   scopeChips?: string[];
 * }} props
 */
export function SearchAlertSignupModal({
  onClose,
  searchPath,
  scopeLabel,
  scopeChips = [],
}) {
  const titleId = useId();
  const emailId = useId();
  const emailRef = useRef(null);
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  const heading =
    typeof scopeLabel === 'string' && scopeLabel.trim() !== ''
      ? scopeLabel.trim()
      : 'this search';

  const chips = Array.isArray(scopeChips)
    ? scopeChips.filter((c) => typeof c === 'string' && c.trim() !== '')
    : [];

  useEffect(() => {
    setEmail('');
    setError(null);
    setSubmitted(false);
    setSubmitting(false);
    const t = window.setTimeout(() => {
      emailRef.current?.focus();
    }, 0);
    return () => window.clearTimeout(t);
  }, [searchPath]);

  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const res = await fetch('/api/search-alert-signup.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim(),
          searchPath,
          scopeLabel: heading,
        }),
      });
      await jsonOrThrow(res);
      setSubmitted(true);
      trackGenerateLead('search_alert');
    } catch (err) {
      setError(describeLoadFailure(err));
    } finally {
      setSubmitting(false);
    }
  }

  return createPortal(
    <div
      className="favorite-modal__backdrop"
      role="presentation"
      onMouseDown={(ev) => {
        if (ev.target === ev.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className="favorite-modal favorite-modal--search-alert"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <div className="favorite-modal__header search-alert-modal__header">
          <div className="search-alert-modal__heading">
            <div className="search-alert-panel__icon" aria-hidden="true">
              <BellIcon />
            </div>
            <h2 id={titleId} className="search-alert-panel__title">
              Get alerts for {heading}
            </h2>
          </div>
          <button
            type="button"
            className="favorite-modal__close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className="favorite-modal__body search-alert-modal__body">
          {submitted ? (
            <>
              <p className="search-alert-panel__success" role="status">
                {searchAlertSignupSuccessMessage(heading)}
              </p>
              <div className="search-alert-modal__actions">
                <button
                  type="button"
                  className="search-alert-panel__submit search-alert-modal__close-btn"
                  onClick={onClose}
                >
                  Close
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="search-alert-panel__lede">
                Leave your email and we&apos;ll keep an eye on {heading} for you.
              </p>

              {chips.length > 0 && (
                <ul className="search-alert-panel__chips" aria-label="Alert scope">
                  {chips.map((chip) => (
                    <li key={chip} className="search-alert-panel__chip">
                      {chip}
                    </li>
                  ))}
                </ul>
              )}

              <ul className="search-alert-panel__benefits">
                {SEARCH_ALERT_BENEFITS.map(({ text, Icon }) => (
                  <li key={text} className="search-alert-panel__benefit">
                    <span className="search-alert-panel__benefit-icon" aria-hidden="true">
                      <Icon />
                    </span>
                    <span>{text}</span>
                  </li>
                ))}
              </ul>

              {error !== null && (
                <p className="search-alert-panel__error" role="alert">
                  {error}
                </p>
              )}

              <form
                className="search-alert-panel__form search-alert-modal__form"
                onSubmit={handleSubmit}
                noValidate
              >
                <label className="sr-only" htmlFor={emailId}>
                  Email for search alerts
                </label>
                <input
                  ref={emailRef}
                  id={emailId}
                  name="email"
                  className="search-alert-panel__input"
                  type="email"
                  autoComplete="email"
                  inputMode="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(ev) => setEmail(ev.target.value)}
                  required
                  maxLength={255}
                  disabled={submitting}
                />
                <button
                  type="submit"
                  className="search-alert-panel__submit"
                  disabled={submitting}
                >
                  {submitting ? 'Joining…' : 'Get email alerts'}
                </button>
              </form>

              <FacebookGeneralDealsNudge
                location="search_alert_modal"
                className="search-alert-panel__facebook--bottom"
              />
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
